import type {
  CellFormulaValue,
  CellHyperlinkValue,
  Workbook,
  Worksheet,
} from 'exceljs';

const FILE_NAME_INVALID_CHARS = /[\\/:*?"<>|]/g;
const FORMULA_STRING_MAX_LENGTH = 255;
export const PROMPT_ROW_BASE_HEIGHT = 24;
export const PROMPT_ROW_LINE_HEIGHT = 18;

export const sanitizeExcelFilename = (title: string): string =>
  title.replace(FILE_NAME_INVALID_CHARS, '_');

// `cell.value = { text, hyperlink: url }` だと、ExcelJS は `#` を `%23` にエンコードしてしまうので
// `HYPERLINK` を使う
export const externalUrlHyperlinkValue = (
  url: string,
  text: string,
): CellFormulaValue | CellHyperlinkValue => {
  // Excel の数式内文字列ではダブルクォートを 2 個重ねてエスケープする
  const escapeFormulaString = (value: string): string =>
    value.replace(/"/g, '""');
  // 数式内の文字列が 255 文字を超えると Excel で #VALUE! になり、
  // 値を読むマクロが止まるため、`#` のエンコードには目をつぶって数式を使わない
  if (
    escapeFormulaString(url).length > FORMULA_STRING_MAX_LENGTH ||
    escapeFormulaString(text).length > FORMULA_STRING_MAX_LENGTH
  ) {
    return { text, hyperlink: url };
  }
  return {
    formula: `HYPERLINK("${escapeFormulaString(url)}","${escapeFormulaString(text)}")`,
    result: text,
    date1904: false,
  };
};

export const getPromptRowHeight = (text?: string | null): number => {
  const lineCount = Math.max((text ?? '').trim().split('\n').length, 1);
  return PROMPT_ROW_BASE_HEIGHT + (lineCount - 1) * PROMPT_ROW_LINE_HEIGHT;
};

export interface CreateWorkbookOptions {
  sheetName: string;
  creator?: string;
  freezeHeaderRow?: boolean;
  fitToPage?: boolean;
}

export interface DownloadWorkbookOptions {
  filename: string;
  format: 'excel' | 'csv';
  csvOptions?: {
    writeBOM?: boolean;
  };
}

/**
 * WorkbookとWorksheetを作成する
 * @param options - ワークブック作成オプション
 * @returns workbookとworksheetのオブジェクト
 */
export const createWorkbookWithSheet = async (
  options: CreateWorkbookOptions,
): Promise<{ workbook: Workbook; worksheet: Worksheet }> => {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.default.Workbook();
  workbook.creator = options.creator ?? 'Stockmark';

  const worksheetOptions: {
    pageSetup?: { fitToPage: boolean };
    views?: { state: 'frozen'; ySplit: number }[];
  } = {};

  if (options.fitToPage !== false) {
    worksheetOptions.pageSetup = { fitToPage: true };
  }

  if (options.freezeHeaderRow !== false) {
    worksheetOptions.views = [{ state: 'frozen', ySplit: 1 }];
  }

  const worksheet = workbook.addWorksheet(options.sheetName, worksheetOptions);

  return { workbook, worksheet };
};

/**
 * aタグを作成してダウンロードを実行する共通処理
 * @param url - ダウンロードするファイルのURL
 * @param filename - ダウンロード時のファイル名
 */
export const triggerDownload = (url: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Workbookをバッファに変換してダウンロードする
 * @param workbook - ExcelJSのWorkbookインスタンス
 * @param options - ダウンロードオプション
 */
export const downloadWorkbook = async (
  workbook: Workbook,
  options: DownloadWorkbookOptions,
): Promise<void> => {
  const buffer =
    options.format === 'excel'
      ? await workbook.xlsx.writeBuffer()
      : await workbook.csv.writeBuffer({
          formatterOptions: options.csvOptions,
        });

  const mimeType =
    options.format === 'excel'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'text/csv';

  const blob = new Blob([buffer], { type: mimeType });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, options.filename);
  URL.revokeObjectURL(url);
};
